import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import { LOGO_URL } from "../lib/api";
import LanguageSwitcher from "./LanguageSwitcher";
import { Button } from "./ui/button";
import { LogOut, Users, Video, Layers, User, Shield, Sparkles, Globe2 } from "lucide-react";

const links = [
  { to: "/app", key: "nav.match", tid: "match", icon: Video },
  { to: "/friends", key: "nav.friends", tid: "friends", icon: Users },
  { to: "/flashcards", key: "nav.flashcards", tid: "flashcards", icon: Layers },
  { to: "/profile", key: "nav.profile", tid: "profile", icon: User },
];

export default function Navbar() {
  const { user, appUserLogout } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const isAdmin = user && user.role === "admin";

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border/60">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link to={user ? "/app" : "/"} className="flex items-center" data-testid="nav-logo">
          <span className="h-11 w-52 overflow-hidden flex items-center"
            style={{ WebkitMaskImage: "linear-gradient(to right, transparent 0%, #000 5%, #000 96%, transparent 100%)", maskImage: "linear-gradient(to right, transparent 0%, #000 5%, #000 96%, transparent 100%)" }}>
            <img src={LOGO_URL} alt="SpeakStudio" className="w-full h-full object-cover object-center" />
          </span>
        </Link>

        {user ? (
          <>
            <nav className="hidden md:flex items-center gap-1">
              {links.map((l) => {
                const active = pathname === l.to;
                return (
                  <Link key={l.to} to={l.to} data-testid={`nav-${l.tid}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2 transition-colors ${active ? "bg-primary/10 text-primary" : "text-foreground/70 hover:text-foreground hover:bg-muted"}`}>
                    <l.icon className="w-4 h-4" /> {t(l.key)}
                  </Link>
                );
              })}
              {isAdmin && (
                <Link to="/admin" data-testid="nav-admin"
                  className={`px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2 transition-colors ${pathname === "/admin" ? "bg-primary/10 text-primary" : "text-foreground/70 hover:text-foreground hover:bg-muted"}`}>
                  <Shield className="w-4 h-4" /> Admin
                </Link>
              )}
            </nav>
            <div className="flex items-center gap-3">
              <Link to="/vision" data-testid="nav-vision" className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold text-secondary hover:bg-secondary/10 transition-colors">
                <Globe2 className="w-4 h-4" /> Our Vision
              </Link>
              <LanguageSwitcher />
              <span className="hidden sm:flex items-center gap-1.5 text-sm font-semibold bg-secondary/15 text-secondary rounded-full px-3 py-1.5" data-testid="nav-points">
                <Sparkles className="w-3.5 h-3.5" /> {user.points ?? 0} {t("nav.points")}
              </span>
              <span className="hidden sm:block text-sm font-medium">{user.name}</span>
              <Button variant="ghost" size="icon" className="rounded-full" data-testid="nav-logout"
                onClick={async () => { await appUserLogout(); nav("/"); }}>
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </>
        ) : (
          <div className="flex items-center gap-2">
            <Link to="/vision" data-testid="nav-vision-public" className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold text-secondary hover:bg-secondary/10 transition-colors">
              <Globe2 className="w-4 h-4" /> Our Vision
            </Link>
            <LanguageSwitcher />
            <Button variant="ghost" className="rounded-full" onClick={() => nav("/login")} data-testid="nav-login">{t("nav.login")}</Button>
            <Button className="rounded-full" onClick={() => nav("/register")} data-testid="nav-signup">{t("nav.signup")}</Button>
          </div>
        )}
      </div>
    </header>
  );
}
